import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { VideoCamera, Plus, Clock, LinkSimple, Users, Play, CalendarBlank, Copy, UserPlus, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LiveMeetingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [directory, setDirectory] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");

  const [form, setForm] = useState({
    title: "", description: "", scheduled_at: "", duration: 60,
    meeting_link: "", meeting_type: "scheduled",
    invited_user_ids: [], invited_roles: [],
  });
  const [inviteForm, setInviteForm] = useState({ user_ids: [], roles: [] });
  const [creating, setCreating] = useState(false);

  const fetchMeetings = () => {
    api.get("/meetings").then(r => { setMeetings(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchMeetings();
    api.get("/users/directory").then(r => setDirectory(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ title: "", description: "", scheduled_at: "", duration: 60, meeting_link: "", meeting_type: "scheduled", invited_user_ids: [], invited_roles: [] });
    setSearchQuery("");
  };

  const createMeeting = async () => {
    if (!form.title) { toast.error("Meeting title is required"); return; }
    if (form.meeting_type === "scheduled" && !form.scheduled_at) { toast.error("Please set a date and time"); return; }
    setCreating(true);
    try {
      const payload = { ...form };
      if (form.meeting_type === "instant") {
        payload.scheduled_at = new Date().toISOString();
      }
      await api.post("/meetings", payload);
      toast.success(form.meeting_type === "instant" ? "Meeting started!" : "Meeting scheduled!");
      setCreateOpen(false);
      resetForm();
      fetchMeetings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create meeting"); }
    setCreating(false);
  };

  const updateStatus = async (meetingId, status) => {
    try {
      await api.put(`/meetings/${meetingId}/status`, { status });
      toast.success(`Meeting ${status}`);
      fetchMeetings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const joinMeeting = async (meeting) => {
    navigate(`/meetings/${meeting.id}/room`);
  };

  const sendInvites = async () => {
    if (!selectedMeeting) return;
    try {
      await api.post(`/meetings/${selectedMeeting.id}/invite`, inviteForm);
      toast.success("Invitations sent!");
      setInviteOpen(false);
      setInviteForm({ user_ids: [], roles: [] });
      fetchMeetings();
    } catch (e) { toast.error("Failed to send invites"); }
  };

  const copyInviteLink = (meeting) => {
    const guestLink = `${window.location.origin}/meetings/${meeting.id}/guest`;
    navigator.clipboard.writeText(guestLink);
    toast.success("Guest meeting link copied! Share this with anyone.");
  };

  const toggleInviteUser = (id) => {
    setInviteForm(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(id) ? prev.user_ids.filter(u => u !== id) : [...prev.user_ids, id]
    }));
  };

  const toggleFormInviteUser = (id) => {
    setForm(prev => ({
      ...prev,
      invited_user_ids: prev.invited_user_ids.includes(id)
        ? prev.invited_user_ids.filter(u => u !== id)
        : [...prev.invited_user_ids, id]
    }));
  };

  const toggleFormRole = (role) => {
    setForm(prev => ({
      ...prev,
      invited_roles: prev.invited_roles.includes(role)
        ? prev.invited_roles.filter(r => r !== role)
        : [...prev.invited_roles, role]
    }));
  };

  const userId = user?.id || user?._id;
  const now = new Date();
  const upcomingMeetings = meetings.filter(m => m.status === "scheduled" || m.status === "live");
  const pastMeetings = meetings.filter(m => m.status === "ended" || m.status === "cancelled");

  const roleOptions = [
    { value: "all", label: "Everyone" },
    { value: "student", label: "All Students" },
    { value: "instructor", label: "All Instructors" },
    { value: "admin", label: "All Admins" },
  ];

  const otherUsers = directory.filter(u => u.id !== userId);
  const filteredUsers = otherUsers.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColors = {
    scheduled: "bg-amber-100 text-amber-800",
    live: "bg-green-100 text-green-800",
    ended: "bg-slate-100 text-slate-600",
    cancelled: "bg-red-100 text-red-700",
  };

  const MeetingCard = ({ meeting }) => {
    const isCreator = meeting.created_by === userId;
    return (
      <Card className="border border-slate-200 rounded-none shadow-sm hover:border-slate-300 transition-colors" data-testid={`meeting-card-${meeting.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {meeting.title}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">by {meeting.creator_name}</p>
            </div>
            <Badge className={`rounded-none text-[10px] shrink-0 ${statusColors[meeting.status] || "bg-slate-100"}`}>
              {meeting.status.toUpperCase()}
            </Badge>
          </div>

          {meeting.description && (
            <p className="text-sm text-slate-600 mb-3 line-clamp-2">{meeting.description}</p>
          )}

          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-4">
            <span className="flex items-center gap-1"><CalendarBlank size={13} /> {new Date(meeting.scheduled_at).toLocaleString()}</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {meeting.duration} min</span>
            <span className="flex items-center gap-1"><Users size={13} /> {meeting.participants?.length || 0} joined</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {meeting.status === "live" && (
              <Button size="sm" onClick={() => joinMeeting(meeting)} className="bg-green-700 text-white hover:bg-green-800 rounded-none gap-1 text-xs" data-testid={`join-meeting-${meeting.id}`}>
                <Play size={13} weight="fill" /> Join Now
              </Button>
            )}
            {meeting.status === "scheduled" && meeting.meeting_link && (
              <Button size="sm" onClick={() => joinMeeting(meeting)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1 text-xs" data-testid={`join-link-${meeting.id}`}>
                <LinkSimple size={13} /> Join Link
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => copyInviteLink(meeting)} className="rounded-none gap-1 text-xs" data-testid={`copy-link-${meeting.id}`}>
              <Copy size={13} /> Copy Link
            </Button>
            {(isCreator || ["super_admin", "admin"].includes(user?.role)) && (
              <>
                <Button size="sm" variant="outline" onClick={() => { setSelectedMeeting(meeting); setInviteOpen(true); }} className="rounded-none gap-1 text-xs" data-testid={`invite-btn-${meeting.id}`}>
                  <UserPlus size={13} /> Invite
                </Button>
                {meeting.status === "scheduled" && (
                  <Button size="sm" onClick={() => updateStatus(meeting.id, "live")} className="bg-green-700 text-white hover:bg-green-800 rounded-none gap-1 text-xs" data-testid={`start-meeting-${meeting.id}`}>
                    <Play size={13} /> Start
                  </Button>
                )}
                {meeting.status === "live" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(meeting.id, "ended")} className="rounded-none gap-1 text-xs border-red-300 text-red-600 hover:bg-red-50" data-testid={`end-meeting-${meeting.id}`}>
                    <XCircle size={13} /> End
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="live-meeting-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Virtual Meetings</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Live Meetings
            </h1>
          </div>
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            {["super_admin", "admin", "instructor"].includes(user?.role) && (
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="create-meeting-btn">
                  <Plus size={16} weight="bold" /> New Meeting
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-lg rounded-none max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Create Meeting</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                {/* Meeting Type */}
                <div className="flex gap-1 bg-slate-100 p-1">
                  <button
                    onClick={() => setForm({ ...form, meeting_type: "scheduled" })}
                    data-testid="type-scheduled"
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${form.meeting_type === "scheduled" ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-200"}`}
                  >
                    <CalendarBlank size={14} /> Schedule
                  </button>
                  <button
                    onClick={() => setForm({ ...form, meeting_type: "instant" })}
                    data-testid="type-instant"
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${form.meeting_type === "instant" ? "bg-green-700 text-white" : "text-slate-600 hover:bg-slate-200"}`}
                  >
                    <Play size={14} weight="fill" /> Start Now
                  </button>
                </div>

                <div>
                  <Label className="text-sm">Meeting Title</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. Weekly Cohort Check-in" data-testid="meeting-title-input" />
                </div>
                <div>
                  <Label className="text-sm">Description (optional)</Label>
                  <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-none" rows={2} placeholder="Meeting agenda..." data-testid="meeting-desc-input" />
                </div>
                {form.meeting_type === "scheduled" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Date & Time</Label>
                      <Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1 rounded-none" data-testid="meeting-datetime-input" />
                    </div>
                    <div>
                      <Label className="text-sm">Duration (min)</Label>
                      <Input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 60 })} className="mt-1 rounded-none" min={15} step={15} data-testid="meeting-duration-input" />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-sm">Meeting Link (Zoom, Google Meet, etc.)</Label>
                  <Input value={form.meeting_link} onChange={e => setForm({ ...form, meeting_link: e.target.value })} className="mt-1 rounded-none" placeholder="https://zoom.us/j/..." data-testid="meeting-link-input" />
                </div>

                <Separator />

                {/* Invite by Role */}
                <div>
                  <Label className="text-sm mb-2 block">Invite by Role</Label>
                  <div className="flex flex-wrap gap-2">
                    {roleOptions.map(r => (
                      <button
                        key={r.value}
                        onClick={() => toggleFormRole(r.value)}
                        className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                          form.invited_roles.includes(r.value) ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Invite Specific Users */}
                <div>
                  <Label className="text-sm">Invite Specific People</Label>
                  <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="mt-1 rounded-none mb-2" />
                  <div className="max-h-32 overflow-y-auto border border-slate-200 p-1 space-y-0.5">
                    {filteredUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={form.invited_user_ids.includes(u.id)} onChange={() => toggleFormInviteUser(u.id)} className="accent-blue-800" />
                        <span className="truncate">{u.name}</span>
                        <Badge variant="outline" className="ml-auto text-[10px] rounded-none">{u.role}</Badge>
                      </label>
                    ))}
                  </div>
                </div>

                <Button onClick={createMeeting} disabled={creating} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="submit-meeting-btn">
                  <VideoCamera size={16} /> {creating ? "Creating..." : form.meeting_type === "instant" ? "Start Meeting Now" : "Schedule Meeting"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-slate-100 mb-6">
            <TabsTrigger value="upcoming" className="rounded-none text-sm" data-testid="upcoming-tab">
              Upcoming & Live {upcomingMeetings.filter(m => m.status === "live").length > 0 && (
                <Badge className="ml-2 rounded-none text-xs bg-green-700">{upcomingMeetings.filter(m => m.status === "live").length} Live</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-none text-sm" data-testid="past-tab">Past</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1,2,3].map(i => <div key={i} className="h-40 bg-slate-100 animate-pulse" />)}
              </div>
            ) : upcomingMeetings.length === 0 ? (
              <div className="text-center py-16">
                <VideoCamera size={48} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No upcoming meetings</p>
                <p className="text-xs text-slate-400 mt-1">Create a new meeting to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingMeetings.map(m => <MeetingCard key={m.id} meeting={m} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastMeetings.length === 0 ? (
              <div className="text-center py-16">
                <Clock size={48} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No past meetings</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pastMeetings.map(m => <MeetingCard key={m.id} meeting={m} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Invite Dialog */}
        <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) setInviteForm({ user_ids: [], roles: [] }); }}>
          <DialogContent className="sm:max-w-md rounded-none max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                Invite to: {selectedMeeting?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-sm mb-2 block">Invite by Role</Label>
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map(r => (
                    <button
                      key={r.value}
                      onClick={() => setInviteForm(prev => ({
                        ...prev,
                        roles: prev.roles.includes(r.value) ? prev.roles.filter(x => x !== r.value) : [...prev.roles, r.value]
                      }))}
                      className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                        inviteForm.roles.includes(r.value) ? "bg-blue-800 text-white border-blue-800" : "border-slate-300 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm">Invite Specific People ({inviteForm.user_ids.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border border-slate-200 p-1 mt-1 space-y-0.5">
                  {otherUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={inviteForm.user_ids.includes(u.id)} onChange={() => toggleInviteUser(u.id)} className="accent-blue-800" />
                      <span className="truncate">{u.name}</span>
                      <Badge variant="outline" className="ml-auto text-[10px] rounded-none">{u.role}</Badge>
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={sendInvites} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="send-invites-btn">
                <UserPlus size={16} /> Send Invitations
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
